import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCompareOnboarding } from "./hooks/useCompareOnboarding";

interface Props {
  isCompareModeActive: boolean;
  enabled: boolean;
  chartWrapRef: React.RefObject<HTMLDivElement>;
  chartRef: React.RefObject<any>;
  latestHoverRef: React.MutableRefObject<any>;
}

export function CompareOnboardingOverlay({
  isCompareModeActive,
  enabled,
  chartWrapRef,
  chartRef,
  latestHoverRef,
}: Props) {
  const { isVisible, activePoint } = useCompareOnboarding(
    isCompareModeActive,
    chartWrapRef,
    chartRef,
    latestHoverRef,
  );

  const [rect, setRect] = useState<DOMRect | null>(null);
  const [flip, setFlip] = useState<{ x: number; y: number }>({ x: 15, y: -42 });
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [exiting, setExiting] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [stickyPoint, setStickyPoint] = useState<{ x: number; y: number; dataIndex: number } | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snapshot activePoint while visible — hook nulls it at hide-time, but we
  // need the position to keep rendering during the exit animation.
  useEffect(() => {
    if (activePoint) setStickyPoint(activePoint);
  }, [activePoint]);

  useEffect(() => {
    if (isVisible) {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setExiting(false);
      setRendered(true);
    } else if (rendered) {
      setExiting(true);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      exitTimerRef.current = setTimeout(() => {
        setRendered(false);
        setExiting(false);
        setStickyPoint(null);
        exitTimerRef.current = null;
      }, 1150);
    }
    return () => {
      if (exitTimerRef.current && isVisible) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [isVisible, rendered]);

  useEffect(() => {
    if (!rendered) return;
    const el = chartWrapRef.current;
    if (!el) return;
    setRect(el.getBoundingClientRect());
    const onScroll = () => {
      const node = chartWrapRef.current;
      if (node) setRect(node.getBoundingClientRect());
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [rendered, chartWrapRef]);

  useLayoutEffect(() => {
    if (!isVisible || !activePoint || !rect) return;
    const tipBounds = document
      .querySelector(".remix-tt .enhanced-tooltip")
      ?.closest(".remix-tt") as HTMLElement | null;
    if (!tipBounds) {
      setFlip({ x: 15, y: -42 });
      return;
    }
    const tooltipRect = tipBounds.getBoundingClientRect();
    if (
      !tooltipRect ||
      (tooltipRect.width === 0 && tooltipRect.height === 0)
    ) {
      setFlip({ x: 15, y: -42 });
      return;
    }

    const pointPageX = rect.left + activePoint.x;
    const pointPageY = rect.top + activePoint.y;

    // Default tip box ~ 215w × 32h at (point + 15, point - 42)
    const tipW = 215;
    const tipH = 32;

    const candidates = [
      { x: 15, y: -42 },          // top-right
      { x: -15 - tipW, y: -42 },  // top-left
      { x: 15, y: 18 },           // bottom-right
      { x: -15 - tipW, y: 18 },   // bottom-left
    ];

    const intersects = (cx: number, cy: number) => {
      const left = pointPageX + cx;
      const top = pointPageY + cy;
      const right = left + tipW;
      const bottom = top + tipH;
      return !(
        right < tooltipRect.left ||
        left > tooltipRect.right ||
        bottom < tooltipRect.top ||
        top > tooltipRect.bottom
      );
    };

    const pick = candidates.find((c) => !intersects(c.x, c.y)) ?? candidates[0];
    setFlip(pick);
  }, [isVisible, activePoint, rect]);

  const point = activePoint ?? stickyPoint;
  if (!enabled || isCompareModeActive || !rendered || !point || !rect) {
    return null;
  }

  const left = rect.left + window.scrollX + point.x;
  const top = rect.top + window.scrollY + point.y;

  return createPortal(
    <div
      ref={overlayRef}
      className={`remix-compare-onboarding-overlay remix-compare-onboarding-overlay--portal remix-tooltip-vars${exiting ? " remix-compare-onboarding-overlay--exit" : ""}`}
      style={
        {
          left,
          top,
          ["--tip-offset-x" as any]: `${flip.x}px`,
          ["--tip-offset-y" as any]: `${flip.y}px`,
        } as React.CSSProperties
      }
    >
      <div className="remix-compare-halo"></div>
      <div className="remix-compare-tip-floating">
        <div className="remix-compare-tip-icon-wrap">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1.55.76 2.91 1.5 3.5.76.76 1.23 1.52 1.41 2.5" />
          </svg>
        </div>
        <div className="remix-compare-tip-content">
          <span className="remix-compare-tip-prefix">Tip:</span> Double-click to compare
        </div>
      </div>
    </div>,
    document.body,
  );
}
